import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Link,
  Paper,
  Theme,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { formatShipmentBookingTypeLabel } from "../../../../../models/Shipments";
import ShipmentsAPI from "../../../api/ShipmentsAPI";
import { ShipmentDetailsApiRow } from "../../../../../models/Shipments/ShipmentDetails";
import {
  ShipmentStatus,
  ShipmentStatusEnum,
} from "../../../../../models/Shipments/ShipmentStatus";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { formatWalletDate } from "../swagstore-wallet/walletFormatters";
import ShipmentBoxesSection from "./ShipmentBoxesSection";
import ShipmentProductsSection from "./ShipmentProductsSection";
import LabelSecondary from "../../components/universal-shipment/LabelSecondary";
import {
  getModernPageColors,
  modernDrawerBodySx,
  modernDrawerHeaderSx,
  modernEmptyStateSx,
  modernSectionPaperSx,
} from "../../../../../styles/modernListingPageSx";

const DRAWER_Z_OFFSET = 2000;

type TimelineStepState = "completed" | "active" | "pending";
type TimelineStepVariant = "default" | "negative";

interface TimelineStep {
  key: string;
  label: string;
  date: Date | string | null;
  state: TimelineStepState;
  variant: TimelineStepVariant;
}

interface MilestoneDefinition {
  key: string;
  statusId: ShipmentStatusEnum;
  variant: TimelineStepVariant;
}

const POSITIVE_MILESTONES: MilestoneDefinition[] = [
  { key: "created", statusId: ShipmentStatusEnum.NEW, variant: "default" },
  {
    key: "ready",
    statusId: ShipmentStatusEnum.READY_TO_SHIP,
    variant: "default",
  },
  { key: "shipped", statusId: ShipmentStatusEnum.SHIPPED, variant: "default" },
  {
    key: "ofd",
    statusId: ShipmentStatusEnum.OUT_FOR_DELIVERY,
    variant: "default",
  },
  {
    key: "delivered",
    statusId: ShipmentStatusEnum.DELIVERED,
    variant: "default",
  },
];

const NEGATIVE_MILESTONES: MilestoneDefinition[] = [
  {
    key: "return_requested",
    statusId: ShipmentStatusEnum.RETURN_REQUESTED,
    variant: "negative",
  },
  {
    key: "returned",
    statusId: ShipmentStatusEnum.RETURNED,
    variant: "negative",
  },
  {
    key: "cancelled",
    statusId: ShipmentStatusEnum.CANCELLED,
    variant: "negative",
  },
  {
    key: "undelivered",
    statusId: ShipmentStatusEnum.UNDELIVERED,
    variant: "negative",
  },
  {
    key: "damaged",
    statusId: ShipmentStatusEnum.DAMAGED,
    variant: "negative",
  },
];

const ALL_MILESTONES = [...POSITIVE_MILESTONES, ...NEGATIVE_MILESTONES];

const NEGATIVE_STATUS_IDS = new Set<ShipmentStatusEnum>(
  NEGATIVE_MILESTONES.map((milestone) => milestone.statusId),
);

const getStatusLabel = (statusId: ShipmentStatusEnum) =>
  ShipmentStatus.find((status) => status.id === statusId)?.value ||
  `Status ${statusId}`;

const getMilestoneDate = (
  details: ShipmentDetailsApiRow,
  statusId: ShipmentStatusEnum,
): Date | string | null => {
  switch (statusId) {
    case ShipmentStatusEnum.NEW:
      return details.created_date;
    case ShipmentStatusEnum.SHIPPED:
      return details.shipped_status_date;
    case ShipmentStatusEnum.DELIVERED:
      return details.delivered_status_date;
    default:
      if (details.status === statusId) {
        return details.status_change_date;
      }
      return null;
  }
};

const hasReachedStatus = (
  details: ShipmentDetailsApiRow,
  statusId: ShipmentStatusEnum,
) => {
  const status = details.status ?? ShipmentStatusEnum.NEW;

  if (statusId === ShipmentStatusEnum.NEW) {
    return Boolean(details.created_date);
  }
  if (statusId === ShipmentStatusEnum.SHIPPED) {
    return Boolean(details.shipped_status_date);
  }
  if (statusId === ShipmentStatusEnum.DELIVERED) {
    return Boolean(details.delivered_status_date);
  }
  if (statusId === ShipmentStatusEnum.READY_TO_SHIP) {
    return (
      Boolean(details.shipped_status_date) ||
      (!NEGATIVE_STATUS_IDS.has(status) &&
        status >= ShipmentStatusEnum.READY_TO_SHIP)
    );
  }
  if (statusId === ShipmentStatusEnum.OUT_FOR_DELIVERY) {
    return (
      Boolean(details.delivered_status_date) ||
      (!NEGATIVE_STATUS_IDS.has(status) &&
        status >= ShipmentStatusEnum.OUT_FOR_DELIVERY)
    );
  }

  if (!NEGATIVE_STATUS_IDS.has(status)) {
    return status >= statusId;
  }

  return false;
};

const getCancelledPath = (
  details: ShipmentDetailsApiRow,
): ShipmentStatusEnum[] => {
  const path: ShipmentStatusEnum[] = [ShipmentStatusEnum.NEW];

  if (hasReachedStatus(details, ShipmentStatusEnum.SHIPPED)) {
    path.push(
      ShipmentStatusEnum.READY_TO_SHIP,
      ShipmentStatusEnum.SHIPPED,
      ShipmentStatusEnum.CANCELLED,
    );
    return path;
  }

  if (hasReachedStatus(details, ShipmentStatusEnum.READY_TO_SHIP)) {
    path.push(ShipmentStatusEnum.READY_TO_SHIP, ShipmentStatusEnum.CANCELLED);
    return path;
  }

  path.push(ShipmentStatusEnum.CANCELLED);
  return path;
};

const getDamagedPath = (
  details: ShipmentDetailsApiRow,
): ShipmentStatusEnum[] => {
  const path: ShipmentStatusEnum[] = [ShipmentStatusEnum.NEW];

  if (hasReachedStatus(details, ShipmentStatusEnum.DELIVERED)) {
    path.push(
      ShipmentStatusEnum.READY_TO_SHIP,
      ShipmentStatusEnum.SHIPPED,
      ShipmentStatusEnum.OUT_FOR_DELIVERY,
      ShipmentStatusEnum.DELIVERED,
      ShipmentStatusEnum.DAMAGED,
    );
    return path;
  }

  if (hasReachedStatus(details, ShipmentStatusEnum.SHIPPED)) {
    path.push(
      ShipmentStatusEnum.READY_TO_SHIP,
      ShipmentStatusEnum.SHIPPED,
      ShipmentStatusEnum.DAMAGED,
    );
    return path;
  }

  if (hasReachedStatus(details, ShipmentStatusEnum.READY_TO_SHIP)) {
    path.push(ShipmentStatusEnum.READY_TO_SHIP, ShipmentStatusEnum.DAMAGED);
    return path;
  }

  path.push(ShipmentStatusEnum.DAMAGED);
  return path;
};

const getTimelinePath = (
  details: ShipmentDetailsApiRow,
): ShipmentStatusEnum[] => {
  const status = details.status ?? ShipmentStatusEnum.NEW;

  if (!NEGATIVE_STATUS_IDS.has(status)) {
    return POSITIVE_MILESTONES.map((milestone) => milestone.statusId);
  }

  switch (status) {
    case ShipmentStatusEnum.RETURN_REQUESTED:
      return [
        ShipmentStatusEnum.NEW,
        ShipmentStatusEnum.READY_TO_SHIP,
        ShipmentStatusEnum.SHIPPED,
        ShipmentStatusEnum.RETURN_REQUESTED,
      ];
    case ShipmentStatusEnum.RETURNED:
      return [
        ShipmentStatusEnum.NEW,
        ShipmentStatusEnum.READY_TO_SHIP,
        ShipmentStatusEnum.SHIPPED,
        ShipmentStatusEnum.RETURN_REQUESTED,
        ShipmentStatusEnum.RETURNED,
      ];
    case ShipmentStatusEnum.CANCELLED:
      return getCancelledPath(details);
    case ShipmentStatusEnum.UNDELIVERED:
      return [
        ShipmentStatusEnum.NEW,
        ShipmentStatusEnum.READY_TO_SHIP,
        ShipmentStatusEnum.SHIPPED,
        ShipmentStatusEnum.UNDELIVERED,
      ];
    case ShipmentStatusEnum.DAMAGED:
      return getDamagedPath(details);
    default:
      return [ShipmentStatusEnum.NEW, status];
  }
};

const getStepState = (
  statusId: ShipmentStatusEnum,
  currentStatus: ShipmentStatusEnum,
  path: ShipmentStatusEnum[],
): TimelineStepState => {
  const stepIndex = path.indexOf(statusId);
  const currentIndex = path.indexOf(currentStatus);

  if (stepIndex === -1) {
    return "pending";
  }

  if (stepIndex < currentIndex) {
    return "completed";
  }

  if (stepIndex === currentIndex) {
    if (NEGATIVE_STATUS_IDS.has(currentStatus)) {
      return "completed";
    }
    if (currentStatus === ShipmentStatusEnum.DELIVERED) {
      return "completed";
    }
    return "active";
  }

  return "pending";
};

const getTimelineSteps = (details: ShipmentDetailsApiRow): TimelineStep[] => {
  const currentStatus = details.status ?? ShipmentStatusEnum.NEW;
  const path = getTimelinePath(details);
  const milestoneMap = new Map(
    ALL_MILESTONES.map((milestone) => [milestone.statusId, milestone]),
  );

  return path.map((statusId) => {
    const milestone = milestoneMap.get(statusId);
    const variant = milestone?.variant ?? "default";
    const label =
      statusId === currentStatus && details.status_label
        ? details.status_label
        : getStatusLabel(statusId);

    return {
      key: milestone?.key ?? `status-${statusId}`,
      label,
      date: getMilestoneDate(details, statusId),
      state: getStepState(statusId, currentStatus, path),
      variant,
    };
  });
};

interface ShipmentDetailsDrawerProps {
  open: boolean;
  shipmentId: number | null;
  onClose: () => void;
  shipmentsAPI?: ShipmentsAPI;
}

const formatDetailDate = (value: Date | string | null | undefined) =>
  value ? formatWalletDate(value) : "Pending";

const formatPhone = (
  callingCode: string | null | undefined,
  phone: string | null | undefined,
) => {
  if (!phone) return "—";
  return callingCode ? `${callingCode} ${phone}` : phone;
};

const formatAddress = (details: ShipmentDetailsApiRow) => {
  const lines = [
    details.address1,
    details.address2,
    details.address3,
    details.landmark,
    [details.city, details.state, details.pincode].filter(Boolean).join(", "),
    details.country,
  ].filter(Boolean);

  return lines.length > 0 ? lines.join("\n") : "—";
};

const formatDeclaredValue = (value?: number | null) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return `₹${Number(value).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;
};

const DetailRow: React.FC<{
  label: string;
  value: React.ReactNode;
}> = ({ label, value }) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: 0.25, sm: 2 },
        py: 0.75,
      }}
    >
      <CustomTypography
        variant="body2"
        sx={{ minWidth: { sm: 148 }, color: colors.subtitle, flexShrink: 0 }}
      >
        {label}
      </CustomTypography>
      <CustomTypography
        variant="body2"
        fontWeight={600}
        sx={{
          minWidth: 0,
          color: colors.title,
          flex: 1,
          wordBreak: "break-word",
        }}
      >
        {value}
      </CustomTypography>
    </Box>
  );
};

const getStepColor = (
  step: TimelineStep,
  colors: ReturnType<typeof getModernPageColors>,
  theme: Theme,
) => {
  if (step.variant === "negative" && step.state !== "pending") {
    return colors.error;
  }
  if (step.state === "pending") {
    return theme.palette.customColors.mediumGrayText;
  }
  return colors.accent;
};

const TimelineIcon: React.FC<{
  step: TimelineStep;
  color: string;
}> = ({ step, color }) => {
  if (step.variant === "negative" && step.state !== "pending") {
    return <CancelIcon sx={{ color, fontSize: { xs: 24, sm: 28 } }} />;
  }
  if (step.state === "completed") {
    return <CheckCircleIcon sx={{ color, fontSize: { xs: 24, sm: 28 } }} />;
  }
  if (step.state === "active") {
    return (
      <RadioButtonCheckedIcon sx={{ color, fontSize: { xs: 24, sm: 28 } }} />
    );
  }
  return (
    <RadioButtonUncheckedIcon
      sx={{ color: "text.disabled", fontSize: { xs: 24, sm: 28 } }}
    />
  );
};

const hasBookingBeenAttempted = (details: ShipmentDetailsApiRow) =>
  Boolean(details.booking_state) ||
  Number(details.booking_attempts ?? 0) > 0 ||
  Boolean(details.last_booked_at);

const BookingSection: React.FC<{
  details: ShipmentDetailsApiRow;
}> = ({ details }) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);
  const bookingAttempted = hasBookingBeenAttempted(details);

  return (
    <Paper variant="outlined" sx={modernSectionPaperSx(theme)}>
      <CustomTypography variant="heading7" sx={{ color: colors.title, mb: 1 }}>
        Carrier Details
      </CustomTypography>
      <Divider sx={{ mb: 1 }} />

      <DetailRow
        label="Booking Type"
        value={formatShipmentBookingTypeLabel(details.booking_type)}
      />

      {bookingAttempted ? (
        <>
          <DetailRow
            label="Carrier Ref"
            value={details.carrier_ref_no || "—"}
          />
          <DetailRow
            label="Booking Attempts"
            value={
              details.booking_attempts == null
                ? "—"
                : String(details.booking_attempts)
            }
          />
        </>
      ) : null}
      <DetailRow
        label="Shipping Label"
        value={
          <LabelSecondary
            labelS3Key={details.label_s3_key}
            emptyFallback="—"
            fontSize="inherit"
          />
        }
      />
      <DetailRow
        label="MPS Shipping Label"
        value={
          <LabelSecondary
            labelS3Key={details.mps_label_s3_key}
            emptyFallback="—"
            fontSize="inherit"
          />
        }
      />
    </Paper>
  );
};

const ShipmentTimeline: React.FC<{ details: ShipmentDetailsApiRow }> = ({
  details,
}) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);
  const steps = getTimelineSteps(details);

  return (
    <Paper
      variant="outlined"
      sx={{
        ...modernSectionPaperSx(theme),
        background: `linear-gradient(135deg, ${colors.surface} 0%, ${colors.surfaceMuted} 100%)`,
      }}
    >
      <CustomTypography
        variant="heading7"
        sx={{ color: colors.title, mb: { xs: 1.5, sm: 2 } }}
      >
        Shipment Timeline
      </CustomTypography>

      {/* Mobile: vertical timeline */}
      <Box
        sx={{ display: { xs: "flex", sm: "none" }, flexDirection: "column" }}
      >
        {steps.map((step, index) => {
          const color = getStepColor(step, colors, theme);
          const isLast = index === steps.length - 1;

          return (
            <Box
              key={step.key}
              sx={{
                display: "flex",
                gap: 1.5,
                position: "relative",
                pb: isLast ? 0 : 2,
              }}
            >
              {!isLast ? (
                <Box
                  sx={{
                    position: "absolute",
                    left: 13,
                    top: 28,
                    bottom: 0,
                    width: 2,
                    bgcolor:
                      step.state === "completed"
                        ? color
                        : "action.disabledBackground",
                  }}
                />
              ) : null}
              <Box sx={{ zIndex: 1, flexShrink: 0 }}>
                <TimelineIcon step={step} color={color} />
              </Box>
              <Box sx={{ minWidth: 0, pt: 0.25 }}>
                <CustomTypography
                  variant="body2"
                  fontWeight={700}
                  sx={{
                    color:
                      step.variant === "negative" && step.state !== "pending"
                        ? colors.error
                        : colors.title,
                  }}
                >
                  {step.label}
                </CustomTypography>
                <CustomTypography
                  variant="caption"
                  color={
                    step.state === "pending"
                      ? "text.disabled"
                      : "text.secondary"
                  }
                  sx={{ display: "block", mt: 0.25, wordBreak: "break-word" }}
                >
                  {formatDetailDate(step.date)}
                </CustomTypography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Tablet/desktop: horizontal scrollable timeline */}
      <Box
        sx={{
          display: { xs: "none", sm: "block" },
          overflowX: "auto",
          pb: 0.5,
          mx: -0.5,
          px: 0.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            minWidth: "max-content",
            gap: 0,
          }}
        >
          {steps.map((step, index) => {
            const color = getStepColor(step, colors, theme);
            const isLast = index === steps.length - 1;
            const connectorCompleted =
              step.state === "completed" ||
              (steps[index + 1]?.state !== "pending" && !isLast);

            return (
              <Box
                key={step.key}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  flex: "0 0 auto",
                  minWidth: { sm: 132, md: 148 },
                  maxWidth: { sm: 160, md: 180 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    px: { sm: 0.75, md: 1 },
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    {index > 0 ? (
                      <Box
                        sx={{
                          flex: 1,
                          height: 2,
                          bgcolor: connectorCompleted
                            ? color
                            : "action.disabledBackground",
                          mr: 0.5,
                        }}
                      />
                    ) : (
                      <Box sx={{ flex: 1, mr: 0.5 }} />
                    )}
                    <TimelineIcon step={step} color={color} />
                    {!isLast ? (
                      <Box
                        sx={{
                          flex: 1,
                          height: 2,
                          bgcolor: connectorCompleted
                            ? color
                            : "action.disabledBackground",
                          ml: 0.5,
                        }}
                      />
                    ) : (
                      <Box sx={{ flex: 1, ml: 0.5 }} />
                    )}
                  </Box>
                  <CustomTypography
                    variant="body2"
                    fontWeight={700}
                    sx={{
                      mt: 1,
                      px: 0.5,
                      color:
                        step.variant === "negative" && step.state !== "pending"
                          ? colors.error
                          : colors.title,
                    }}
                  >
                    {step.label}
                  </CustomTypography>
                  <CustomTypography
                    variant="caption"
                    color={
                      step.state === "pending"
                        ? "text.disabled"
                        : "text.secondary"
                    }
                    sx={{ display: "block", mt: 0.25, px: 0.5 }}
                  >
                    {formatDetailDate(step.date)}
                  </CustomTypography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
};

const ShipmentDetailsDrawer: React.FC<ShipmentDetailsDrawerProps> = ({
  open,
  shipmentId,
  onClose,
  shipmentsAPI,
}) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);
  const apiRef = useRef(shipmentsAPI ?? new ShipmentsAPI());
  const requestSeqRef = useRef(0);

  const [details, setDetails] = useState<ShipmentDetailsApiRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const api = useMemo(() => apiRef.current, []);

  const loadDetails = async (id: number, seq?: number) => {
    try {
      const response = await api.getShipmentDetails(id);
      if (seq != null && seq !== requestSeqRef.current) return null;

      if (!response?.success) {
        throw new Error(
          response?.message || "Failed to fetch shipment details",
        );
      }

      setDetails(response.result);
      return response.result;
    } catch (err) {
      throw err;
    }
  };

  useEffect(() => {
    if (!open || !shipmentId) {
      setDetails(null);
      setError(null);
      setLoading(false);
      return;
    }

    const seq = ++requestSeqRef.current;
    setLoading(true);
    setError(null);
    setDetails(null);

    loadDetails(shipmentId, seq)
      .catch((err: any) => {
        if (seq !== requestSeqRef.current) return;
        setDetails(null);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch shipment details. Please try again.",
        );
      })
      .finally(() => {
        if (seq !== requestSeqRef.current) return;
        setLoading(false);
      });
    // loadDetails is defined in render and would retrigger the fetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, open, shipmentId]);

  const recipientName = details
    ? [details.first_name, details.last_name].filter(Boolean).join(" ")
    : "";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480, md: 560, lg: 600 },
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: colors.surface,
        },
      }}
      ModalProps={{
        keepMounted: true,
        sx: {
          zIndex: (muiTheme) => muiTheme.zIndex.drawer + DRAWER_Z_OFFSET,
        },
        slotProps: {
          backdrop: {
            sx: {
              zIndex: (muiTheme) =>
                muiTheme.zIndex.drawer + DRAWER_Z_OFFSET - 1,
            },
          },
        },
      }}
    >
      <Box sx={modernDrawerHeaderSx(theme)}>
        <Box display="flex" gap={1.5} alignItems="center" minWidth={0}>
          <LocalShippingOutlinedIcon
            sx={{ flexShrink: 0, color: colors.accent }}
          />
          <Box minWidth={0}>
            <CustomTypography
              variant="heading5"
              sx={{
                color: colors.title,
                fontSize: { xs: "1rem", sm: "1.25rem" },
              }}
            >
              Shipment Details
            </CustomTypography>
            <CustomTypography
              variant="body2"
              sx={{
                color: colors.subtitle,
                minWidth: { sm: 148 },
                flexShrink: 0,
                mt: 0.75,
                display: "block",
              }}
            >
              {shipmentId ? `Shipment #${shipmentId}` : "Shipment information"}
            </CustomTypography>
          </Box>
        </Box>

        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={modernDrawerBodySx()}>
        {error ? (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Box
            sx={{
              minHeight: 320,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : details ? (
          <>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Chip
                label={details.status_label || "Unknown status"}
                color={
                  details.status != null &&
                  NEGATIVE_STATUS_IDS.has(details.status)
                    ? "error"
                    : "primary"
                }
                size="small"
                sx={{ fontWeight: 600, maxWidth: "100%" }}
              />
              {details.shipment_type_label ? (
                <Chip
                  label={details.shipment_type_label}
                  variant="outlined"
                  size="small"
                />
              ) : null}
              {details.paid != null ? (
                <Chip
                  label={details.paid ? "Paid" : "Unpaid"}
                  color={details.paid ? "success" : "warning"}
                  variant="outlined"
                  size="small"
                />
              ) : null}
            </Box>

            <ShipmentTimeline details={details} />

            <Paper variant="outlined" sx={modernSectionPaperSx(theme)}>
              <CustomTypography
                variant="heading7"
                sx={{ color: colors.title, mb: 1 }}
              >
                Order Notes
              </CustomTypography>
              <Divider sx={{ mb: 1 }} />
              <CustomTypography
                variant="body2"
                sx={{
                  color: details.order_notes?.trim()
                    ? colors.title
                    : colors.subtitle,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {details.order_notes?.trim() || "—"}
              </CustomTypography>
            </Paper>

            <BookingSection details={details} />

            {shipmentId != null && (
              <ShipmentProductsSection
                shipmentId={shipmentId}
                products={details.products}
                inventoryId={details.inventory_id}
                canAdd={false}
                canRemove={false}
                shipmentsAPI={api}
                onProductsChange={() => {
                  // Drawer is view-only; product mutations happen in the edit popup.
                }}
              />
            )}

            {shipmentId != null && (
              <Paper variant="outlined" sx={modernSectionPaperSx(theme)}>
                <ShipmentBoxesSection
                  shipmentId={shipmentId}
                  boxes={details.boxes}
                  canEdit={false}
                  shipmentsAPI={api}
                  onBoxesChange={() => {
                    // Drawer is view-only; box mutations happen in the edit popup.
                  }}
                />
              </Paper>
            )}

            <Paper variant="outlined" sx={modernSectionPaperSx(theme)}>
              <CustomTypography
                variant="heading7"
                sx={{ color: colors.title, mb: 1 }}
              >
                Shipment Overview
              </CustomTypography>
              <Divider sx={{ mb: 1 }} />
              <DetailRow
                label="Customer"
                value={
                  details.customer_name
                    ? `${details.customer_name}${details.customer_id ? ` (#${details.customer_id})` : ""}`
                    : details.customer_id
                      ? `#${details.customer_id}`
                      : "—"
                }
              />
              <DetailRow
                label="Store / Inventory"
                value={
                  [
                    details.store_id ? `Store #${details.store_id}` : null,
                    details.inventory_id
                      ? `Inventory #${details.inventory_id}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ") || "—"
                }
              />
              <DetailRow
                label="AWB"
                value={
                  details.awb ? (
                    details.awb_track_url ? (
                      <Link
                        href={details.awb_track_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {details.awb}
                      </Link>
                    ) : (
                      details.awb
                    )
                  ) : (
                    "—"
                  )
                }
              />
              {details.additional_awb?.length ? (
                <DetailRow
                  label="Additional AWB"
                  value={details.additional_awb.join(", ")}
                />
              ) : null}
              <DetailRow
                label="Delivery Partner"
                value={
                  details.delivery_partner_name ? (
                    details.deliver_partner_tracking_url ? (
                      <Link
                        href={details.deliver_partner_tracking_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {details.delivery_partner_name}
                      </Link>
                    ) : (
                      details.delivery_partner_name
                    )
                  ) : (
                    "—"
                  )
                }
              />
              <DetailRow
                label="Shipment Method"
                value={details.shipment_method || "—"}
              />
              <DetailRow
                label="Shipping Cost"
                value={
                  details.shipping_cost != null
                    ? `₹${details.shipping_cost.toLocaleString("en-IN", {
                        maximumFractionDigits: 2,
                      })}`
                    : "—"
                }
              />
              <DetailRow
                label="Status Changed"
                value={formatDetailDate(details.status_change_date)}
              />
              <DetailRow
                label="Declared Value"
                value={formatDeclaredValue(details.declared_value)}
              />
              <DetailRow
                label="Last Updated"
                value={formatDetailDate(details.modified_date)}
              />
            </Paper>

            <Paper variant="outlined" sx={modernSectionPaperSx(theme)}>
              <CustomTypography
                variant="heading7"
                sx={{ color: colors.title, mb: 1 }}
              >
                Recipient
              </CustomTypography>
              <Divider sx={{ mb: 1 }} />
              <DetailRow label="Name" value={recipientName || "—"} />
              <DetailRow label="Email" value={details.email || "—"} />
              <DetailRow
                label="Phone"
                value={formatPhone(details.calling_code, details.phone)}
              />
              <DetailRow
                label="Address"
                value={
                  <CustomTypography
                    variant="body2"
                    sx={{ whiteSpace: "pre-line" }}
                  >
                    {formatAddress(details)}
                  </CustomTypography>
                }
              />
            </Paper>

            <Paper variant="outlined" sx={modernSectionPaperSx(theme)}>
              <CustomTypography
                variant="heading7"
                sx={{ color: colors.title, mb: 1 }}
              >
                Placed By
              </CustomTypography>
              <Divider sx={{ mb: 1 }} />
              <DetailRow label="User" value={details.placed_by_user || "—"} />
              <DetailRow
                label="Admin"
                value={details.placed_by_admin_name || "—"}
              />
            </Paper>
          </>
        ) : !error ? (
          <Box sx={{ ...modernEmptyStateSx(theme), minHeight: 240 }}>
            <CustomTypography variant="body2">
              Select a shipment to view details.
            </CustomTypography>
          </Box>
        ) : null}
      </Box>
    </Drawer>
  );
};

export default ShipmentDetailsDrawer;
